import { useEffect, useState, type FormEvent } from "react";
import { api, message, type Client, type UserRow } from "../api";

export function Users({ meId }: { meId: string }) {
  const [rows, setRows] = useState<UserRow[] | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState<"admin" | "user">("user");
  const [clientIds, setClientIds] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** A just-issued link, shown once: for a new user or a reset. */
  const [link, setLink] = useState<{ email: string; url: string; kind: "invite" | "reset" } | null>(null);
  const [copied, setCopied] = useState(false);

  async function load() {
    try {
      const [u, c] = await Promise.all([
        api.get<UserRow[]>("/api/users"),
        api.get<Client[]>("/api/clients"),
      ]);
      setRows(u); setClients(c);
    } catch (err) { setError(message(err)); setRows([]); }
  }
  useEffect(() => { void load(); }, []);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const res = await api.post<{ email: string; inviteUrl: string }>(
        "/api/users", { email, name, role, clientIds },
      );
      setLink({ email: res.email, url: res.inviteUrl, kind: "invite" }); setCopied(false);
      setEmail(""); setName(""); setRole("user"); setClientIds([]);
      setOpen(false);
      await load();
    } catch (err) { setError(message(err)); }
    finally { setBusy(false); }
  }

  /** Forgotten password, or (lostDevice) a lost passkey as well. */
  async function newLink(u: UserRow, lostDevice: boolean) {
    const ok = window.confirm(lostDevice
      ? `Reset ${u.name}'s sign-in for a lost device?\n\nTheir passkeys and current password stop working ` +
        "immediately and they are signed out everywhere. They get back in only through the link you send them."
      : `Create a new sign-in link for ${u.name}?\n\nTheir current password keeps working until they use it. ` +
        "Any earlier link stops working.");
    if (!ok) return;
    setError(null);
    try {
      const res = await api.post<{ url: string }>(`/api/users/${u.id}/password-link`, { removePasskeys: lostDevice });
      setLink({ email: u.email, url: res.url, kind: "reset" }); setCopied(false);
      await load();
    } catch (err) { setError(message(err)); }
  }

  async function copyLink() {
    if (!link) return;
    try { await navigator.clipboard.writeText(link.url); setCopied(true); }
    catch { setError("Couldn't copy automatically — select the link and copy it."); }
  }

  async function toggleActive(u: UserRow) {
    try {
      await api.patch(`/api/users/${u.id}`, { active: !u.active });
      await load();
    } catch (err) { setError(message(err)); }
  }

  return (
    <>
      <div className="pagehead">
        <h1>Users</h1>
        <button className="btn btn--inline" onClick={() => { setOpen((v) => !v); setLink(null); }}>
          {open ? "Cancel" : "New user"}
        </button>
      </div>

      {error && <div className="alert" role="alert">{error}</div>}

      {link && (
        <div className="card notice">
          <p>
            {link.kind === "invite"
              ? <><strong>{link.email}</strong> created. Send them this link to set their own password.</>
              : <>New sign-in link for <strong>{link.email}</strong>.</>}
            {" "}It works once, for 72 hours, and is shown only now.
          </p>
          <p className="mono small linkbox">{link.url}</p>
          <p>
            <button className="btn btn--inline btn--small" onClick={() => void copyLink()}>
              {copied ? "Copied" : "Copy link"}
            </button>{" "}
            <button className="linkbtn" onClick={() => setLink(null)}>Done</button>
          </p>
          <p className="muted small">
            Nobody else ever sees their password — not even you. Ask them to add a
            passkey from their Account page once they're in.
          </p>
        </div>
      )}

      {open && (
        <form className="card card--form" onSubmit={submit}>
          <div className="field">
            <label htmlFor="uname">Name</label>
            <input id="uname" required value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="uemail">Email</label>
            <input id="uemail" type="email" required value={email}
                   onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="urole">Role</label>
            <select id="urole" value={role}
                    onChange={(e) => setRole(e.target.value as "admin" | "user")}>
              <option value="user">User — can create and review meetings</option>
              <option value="admin">Admin — can also manage users and clients</option>
            </select>
          </div>
          {role === "user" && (
            <div className="field">
              <label>Client access</label>
              {clients.length === 0
                ? <p className="hint">No clients exist yet.</p>
                : clients.map((c) => (
                    <label key={c.id} className="check">
                      <input
                        type="checkbox"
                        checked={clientIds.includes(c.id)}
                        onChange={(e) => setClientIds((ids) =>
                          e.target.checked ? [...ids, c.id] : ids.filter((i) => i !== c.id))}
                      />
                      {c.name}
                    </label>
                  ))}
              <p className="hint">Admins can see every client regardless.</p>
            </div>
          )}
          <button className="btn" type="submit" disabled={busy}>
            {busy ? "Creating…" : "Create user"}
          </button>
        </form>
      )}

      {rows === null ? <p className="muted">Loading…</p> : (
        <table className="table">
          <thead>
            <tr><th>Name</th><th>Email</th><th>Role</th><th>Passkey</th><th>Last sign-in</th><th></th></tr>
          </thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.id} className={u.active ? "" : "row--off"}>
                <td>{u.name}</td>
                <td className="mono small">{u.email}</td>
                <td>{u.role === "admin" ? <span className="pill">Admin</span> : "User"}</td>
                <td>
                  {u.mfaRequired
                    ? <span className="ok">Enrolled</span>
                    : <span className="warn">None</span>}
                </td>
                <td className="muted small">
                  {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString() : "never"}
                </td>
                <td className="small">
                  {u.active && (
                    <button className="linkbtn" onClick={() => void newLink(u, false)}>Sign-in link</button>
                  )}
                  {u.active && u.id !== meId && u.mfaRequired && (
                    <>{" · "}<button className="linkbtn" onClick={() => void newLink(u, true)}>Lost device</button></>
                  )}
                  {u.id !== meId && (
                    <>{u.active ? " · " : ""}<button className="linkbtn" onClick={() => void toggleActive(u)}>
                      {u.active ? "Deactivate" : "Reactivate"}
                    </button></>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}
